import React from 'react';
import { Loader2, Lock, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import { api } from '../../services/api';

export const BuybackModal = ({
  buybackFor, setBuybackFor, buybackQuote, setBuybackQuote,
  buybackLoading, submitBuyback, formatCurrency,
}) => (
  <Dialog open={!!buybackFor} onOpenChange={() => { setBuybackFor(null); setBuybackQuote(null); }}>
    <DialogContent className="bg-[#0A0A0A] border-[#262626] max-w-lg max-h-[85vh] overflow-y-auto" data-testid="buyback-modal">
      <DialogHeader>
        <DialogTitle className="font-serif text-white">Un-tokenise {buybackFor?.asset_name}</DialogTitle>
        <p className="text-[11px] text-muted-foreground mt-1">Reclaim 100% ownership and remove this asset from the marketplace.</p>
      </DialogHeader>
      {!buybackQuote ? (
        <div className="flex items-center justify-center py-12"><Loader2 size={28} className="text-gold animate-spin" /></div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-2">
            <div className="p-3 bg-[#0F0F0F] border border-[#1A1A1A] rounded text-center">
              <div className="w-6 h-6 mx-auto mb-2 rounded-full bg-gold/15 flex items-center justify-center text-gold text-xs font-semibold">1</div>
              <p className="text-[10px] text-white font-medium leading-tight">You pay total cost via Stripe</p>
            </div>
            <div className="p-3 bg-[#0F0F0F] border border-[#1A1A1A] rounded text-center">
              <div className="w-6 h-6 mx-auto mb-2 rounded-full bg-emerald-500/15 flex items-center justify-center text-emerald-400 text-xs font-semibold">2</div>
              <p className="text-[10px] text-white font-medium leading-tight">Holders receive +5% premium instantly</p>
            </div>
            <div className="p-3 bg-[#0F0F0F] border border-[#1A1A1A] rounded text-center">
              <div className="w-6 h-6 mx-auto mb-2 rounded-full bg-[#627EEA]/15 flex items-center justify-center text-[#627EEA] text-xs font-semibold">3</div>
              <p className="text-[10px] text-white font-medium leading-tight">You hold 100% again, asset is freed</p>
            </div>
          </div>

          <div className="p-3 bg-amber-500/5 border border-amber-500/20 rounded flex items-start gap-2">
            <AlertTriangle size={14} className="text-amber-400 flex-shrink-0 mt-0.5" />
            <p className="text-[11px] text-amber-200/80 leading-relaxed">
              The token is permanently retired from the Assagart marketplace. Your physical asset is unaffected. The on-chain registry record stays as a permanent provenance trail.
            </p>
          </div>

          {buybackQuote.fractions_outstanding === 0 ? (
            <div className="p-4 bg-[#0F0F0F] border border-[#1A1A1A] rounded text-center">
              <p className="text-white text-sm">No fractions have been sold.</p>
              <p className="text-[11px] text-muted-foreground mt-1">You can un-tokenise this asset for free.</p>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <div className="flex justify-between text-xs"><span className="text-muted-foreground">Fractions outstanding</span><span className="text-white">{buybackQuote.fractions_outstanding.toLocaleString()}</span></div>
                <div className="flex justify-between text-xs"><span className="text-muted-foreground">Current price / fraction</span><span className="text-white">{formatCurrency(buybackQuote.current_price_per_fraction)}</span></div>
                <div className="flex justify-between text-xs"><span className="text-muted-foreground">Buyback price (+{buybackQuote.premium_pct}% premium)</span><span className="text-emerald-400">{formatCurrency(buybackQuote.buyback_price_per_fraction)}</span></div>
                <div className="flex justify-between text-xs"><span className="text-muted-foreground">Total payout to {buybackQuote.holder_count} holder(s)</span><span className="text-white">{formatCurrency(buybackQuote.total_payout_to_holders)}</span></div>
                <div className="flex justify-between text-xs"><span className="text-muted-foreground">Platform fee ({buybackQuote.platform_fee_pct}%)</span><span className="text-white">{formatCurrency(buybackQuote.platform_fee)}</span></div>
                <div className="border-t border-[#1A1A1A] pt-2 flex justify-between"><span className="text-sm text-white font-medium">You pay</span><span className="text-gold text-lg font-semibold">{formatCurrency(buybackQuote.total_cost)}</span></div>
              </div>

              {buybackQuote.holders_missing_payouts?.length > 0 && (
                <div className="p-3 bg-red-500/5 border border-red-500/20 rounded space-y-2">
                  <p className="text-[11px] text-red-300 leading-relaxed">
                    {buybackQuote.holders_missing_payouts.length} holder(s) haven't enabled Stripe payouts yet. Buyback is blocked until they complete onboarding so we can route their funds correctly.
                  </p>
                  <p className="text-[10px] text-red-300/70 leading-relaxed">
                    Affected: {buybackQuote.holders_missing_payouts.slice(0, 3).join(', ')}
                    {buybackQuote.holders_missing_payouts.length > 3 ? ` (+${buybackQuote.holders_missing_payouts.length - 3} more)` : ''}
                  </p>
                  <p className="text-[10px] text-red-300/70 leading-relaxed">
                    Tap <strong>Notify holders</strong> below and we&apos;ll send each of them a one-click Stripe onboarding link.
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full border-red-500/40 text-red-300 hover:bg-red-500/10 text-[11px]"
                    onClick={async () => {
                      try {
                        await api.notifyHoldersToOnboard(buybackFor.id);
                        toast.success('Reminder sent to holders to complete Stripe payout setup.');
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'Could not notify holders.');
                      }
                    }}
                    data-testid="notify-holders-onboard-btn"
                  >
                    Notify holders to enable payouts
                  </Button>
                </div>
              )}
            </>
          )}

          <Button
            className="w-full bg-red-600 hover:bg-red-700 text-white"
            disabled={buybackLoading || (buybackQuote.holders_missing_payouts?.length > 0)}
            onClick={submitBuyback}
            data-testid="confirm-buyback-btn"
          >
            {buybackLoading ? <Loader2 size={14} className="animate-spin mr-2" /> : <Lock size={14} className="mr-2" />}
            {buybackQuote.fractions_outstanding === 0 ? 'Burn token (free)' : `Pay ${formatCurrency(buybackQuote.total_cost)} & Un-tokenise`}
          </Button>

          <p className="text-[10px] text-muted-foreground text-center leading-relaxed">
            This action cannot be undone. You will be redirected to Stripe to complete payment securely.
          </p>
        </div>
      )}
    </DialogContent>
  </Dialog>
);
